import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getCurrentAuthContext, setToken, setUser, getToken, getUser, removeToken, removeUser } from '../../services/auth';
import { devLog, devError } from '../../utils/devLog';
import type { ParishAdminOnboardingPayload } from '../../types/types';

// Tipos
export interface User {
  id: string;
  name: string;
  email: string;
  userType: string;
  /** Role real do Admin (super_admin | admin | contador) */
  role?: string;
  permissions?: string[];
  parishId?: string;
  emailVerified?: boolean;
  emailVerifiedAt?: string | null;
  /** Assistente faseado (só parish_admin típico) */
  parishAdminOnboarding?: ParishAdminOnboardingPayload;
  /** Liberado pelo admin master; sem isso a aba Saque no painel do evento não aparece */
  allowWithdrawalAccess?: boolean;
  /** Master dash: libera “Adicionar organização” no painel */
  allowAdditionalOrganizations?: boolean;
  /** Organizações do parish_admin (multi-org); parishId = ativa */
  organizations?: Array<{
    _id: string;
    name: string;
    slug?: string;
    cpfCnpj?: string | null;
  }>;
  parish?: {
    id: string;
    name: string;
    city: string;
    state: string;
    logoUrl?: string;
    feePercentage: number;
    isMainParish?: boolean;
    sectorAccessLevel?: 'leitura' | 'eventos' | 'operacao';
    /** White-label: omitido = platform */
    brandKey?: 'platform' | 'zuadaria';
    features?: {
      allowEarlyWithdrawalRequest?: boolean;
      allowCreateSubgroup?: boolean;
      maxSubParishes?: number;
      pagarmePixCheckoutEnabled?: boolean;
      withdrawalPanelMode?: 'released' | 'hidden' | 'message';
      withdrawalBlockedMessage?: string;
      allowRefundRequest?: boolean;
      customDomainPagesEnabled?: boolean;
      audienceFunnelBetaEnabled?: boolean;
      audienceFunnelBetaJoinedAt?: string;
    };
    /** true quando onboarding Pagar.me (subconta) está sincronizado e pronto */
    pagarmeRecipientReady?: boolean;
    hasWithdrawalData?: boolean;
  };
}

/** Mapeia role do Admin collection para userType do front. */
function mapAdminUserType(role?: string): string {
  if (role === 'contador') return 'contador';
  // admin e super_admin usam o painel completo (comportamento atual)
  return 'super_admin';
}

function normalizeAdminUser(admin: any): User {
  return {
    id: admin._id,
    name: admin.name,
    email: admin.email,
    userType: mapAdminUserType(admin.role),
    role: admin.role,
    permissions: admin.permissions || [],
  };
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Estado inicial
const initialState: AuthState = {
  token: getToken(),
  isAuthenticated: !!getToken(),
  user: getUser(),
  loading: false,
  error: null,
};

// API base URL
const API_URL = process.env.REACT_APP_API_URL;

// Thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string; userType?: string }, { rejectWithValue }) => {
    try {
      devLog('🔄 [LOGIN] API_URL:', API_URL);
      const context = getCurrentAuthContext();
      devLog('🔄 [LOGIN] Contexto:', context);

      // Determinar a rota baseada no contexto
      const loginUrl = context === 'admin' 
        ? `${API_URL}/admin/auth/login`
        : `${API_URL}/auth/login`;

      devLog('🔄 [LOGIN] POST', loginUrl);

      const response = await axios.post(loginUrl, {
        email: credentials.email,
        password: credentials.password
      });
      
      // Ajustar a resposta baseada no contexto
      let userData;
      if (context === 'admin') {
        // Resposta do admin: { success: true, data: { admin: {...}, token: "..." } }
        userData = {
          token: response.data.data.token,
          user: normalizeAdminUser(response.data.data.admin),
        };
      } else {
        // Resposta do usuário comum: { token: "...", user: {...} }
        userData = response.data;
      }
      
      // Limpar tokens do contexto oposto antes de salvar
      const oppositeContext = context === 'admin' ? 'parish' : 'admin';
      removeToken(oppositeContext);
      removeUser(oppositeContext);

      // Salvar no contexto correto
      setToken(userData.token, context);
      setUser(userData.user, context);

      devLog('✅ [LOGIN] Token salvo no contexto:', context);

      return userData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erro ao fazer login'
      );
    }
  }
);

export const sectorLogin = createAsyncThunk(
  'auth/sectorLogin',
  async (credentials: { slug: string; name: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/sector-login`, credentials);
      removeToken('admin');
      removeUser('admin');
      setToken(response.data.token, 'parish');
      setUser(response.data.user, 'parish');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao fazer login');
    }
  }
);

export const parishService = {
  async checkNameExists(name: string): Promise<boolean> {
    try {
      const response = await axios.get('/api/parishes/check-name', {
        params: { name }
      });
      return response.data.exists;
    } catch (error) {
      devError('Erro ao verificar nome da paróquia:', error);
      return false;
    }
  }
};

export const register = createAsyncThunk(
  'auth/register',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erro ao registrar usuário'
      );
    }
  }
);

export const switchParish = createAsyncThunk(
  'auth/switchParish',
  async (parishId: string, { rejectWithValue }) => {
    try {
      const { default: api } = await import('../../services/api');
      const response = await api.post('/auth/switch-parish', { parishId });
      const data = response.data;
      if (data?.token) {
        setToken(data.token, 'parish');
      }
      if (data?.user) {
        setUser(data.user, 'parish');
      }
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erro ao trocar organização'
      );
    }
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const context = getCurrentAuthContext();
      const token = getToken(context);

      devLog('🔄 [LOAD_USER] Iniciando:', { context, hasToken: !!token });

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      };
      
      const meUrl = context === 'admin' 
        ? `${API_URL}/admin/auth/me`
        : `${API_URL}/auth/me`;

      devLog('🔄 [LOAD_USER] GET', meUrl);

      const response = await axios.get(meUrl, config);
      devLog('✅ [LOAD_USER] Resposta OK');

      // Normalizar shape por contexto para evitar perder userType no refresh.
      if (context === 'admin') {
        const admin = response.data?.data;
        if (!admin?._id) {
          throw new Error('Resposta inválida ao carregar admin');
        }
        return {
          user: normalizeAdminUser(admin),
        };
      }

      return response.data;
    } catch (error: any) {
      devError('❌ [LOAD_USER] Erro ao carregar usuário:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Erro ao carregar usuário'
      );
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      const context = getCurrentAuthContext();
      removeToken(context);
      removeUser(context);
      state.token = null;
      state.isAuthenticated = false;
      state.user = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sectorLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sectorLogin.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(sectorLogin.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(register.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Load User
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        devLog('🔍 [LOAD_USER.fulfilled] Payload:', action.payload);
        // Tentar diferentes estruturas de dados
        const userData = action.payload?.user || action.payload?.data?.user || action.payload;
        state.user = userData;
        const context = getCurrentAuthContext();
        if (userData) {
          setUser(userData, context);
        }
        devLog('✅ [LOAD_USER.fulfilled] user no estado:', state.user);
      })
      .addCase(loadUser.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(switchParish.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchParish.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload?.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(switchParish.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError } = authSlice.actions;

// Seletores
export const selectAuth = (state: any) => state.auth;
export const selectUser = (state: any) => state.auth.user;
export const selectIsAuthenticated = (state: any) => state.auth.isAuthenticated;

export default authSlice.reducer;
